import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  getGoldenThreads,
  getHiddenGems,
  getSurveyStats,
  getCvRoles,
} from '@/services/mockData';
import { DetailShell } from '../../detail/DetailShell';
import { SectionTitle } from '../../detail/SectionTitle';
import { useVideoPopup } from '../../detail/VideoPopup';
import { SurveyFlow } from './SurveyFlow';
import { ResultsOverlay } from './ResultsOverlay';

const EYE = '02 · Brand Builder';

let _nextGemId = 100;
let _nextThreadId = 100;
let _nextCvId = 100;

const MONO_LABEL = {
  fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)',
  letterSpacing: '0.04em', textTransform: 'uppercase',
};
const INPUT = {
  fontFamily: 'inherit', fontSize: 13, background: 'var(--paper-2)',
  border: '1px solid var(--hairline)', padding: '4px 8px',
  color: 'var(--ink)', outline: 'none', width: '100%', boxSizing: 'border-box',
};
const TEXTAREA = { ...INPUT, resize: 'vertical', minHeight: 56 };
const ICON_BTN = {
  ...MONO_LABEL, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 5px',
};

function blankGem() {
  return { id: `gem_new_${++_nextGemId}`, title: '', body: '' };
}
function blankThread() {
  return { id: `thd_new_${++_nextThreadId}`, title: '', body: '' };
}
function blankCvRole() {
  return {
    id: `cvr_new_${++_nextCvId}`,
    start: '', end: '', org: '', title: '',
    activities: '', skills: '', knowledge: '',
    experience: '', insight: '', impact: '',
  };
}

export function BrandDetail({ onClose }) {
  const [gems, setGems] = useState(null);
  const [threads, setThreads] = useState(null);
  const [stats, setStats] = useState(null);
  const [traits, setTraits] = useState(null);
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editGemId, setEditGemId] = useState(null);
  const [editThreadId, setEditThreadId] = useState(null);
  const [editTraitIdx, setEditTraitIdx] = useState(null);
  const [editCvId, setEditCvId] = useState(null);
  const [addingGem, setAddingGem] = useState(false);
  const [addingThread, setAddingThread] = useState(false);
  const [addingTrait, setAddingTrait] = useState(false);
  const [survey, setSurvey] = useState(false);
  const [results, setResults] = useState(false);
  const vp = useVideoPopup();

  useEffect(() => {
    let cancelled = false;
    Promise.all([getHiddenGems(), getGoldenThreads(), getSurveyStats(), getCvRoles()])
      .then(([g, t, s, c]) => {
        if (cancelled) return;
        setGems(g); setThreads(t); setStats(s);
        setTraits(s.topTraits.map(([name]) => name));
        setCv(c);
        setLoading(false);
      })
      .catch((e) => { if (!cancelled) { setError(e); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const updateGem = (id, patch) => setGems((gs) => gs.map((g) => (g.id === id ? { ...g, ...patch } : g)));
  const commitGem = (title, body) => { setGems((gs) => [...gs, { ...blankGem(), title, body }]); setAddingGem(false); };
  const deleteGem = (id) => { setGems((gs) => gs.filter((g) => g.id !== id)); if (editGemId === id) setEditGemId(null); };

  const updateThread = (id, patch) => setThreads((ts) => ts.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  const commitThread = (title, body) => { setThreads((ts) => [...ts, { ...blankThread(), title, body }]); setAddingThread(false); };
  const deleteThread = (id) => { setThreads((ts) => ts.filter((t) => t.id !== id)); if (editThreadId === id) setEditThreadId(null); };

  const updateCv = (id, patch) => setCv((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addCvRole = () => { const r = blankCvRole(); setCv((rs) => [...rs, r]); setEditCvId(r.id); };
  const deleteCvRole = (id) => { setCv((rs) => rs.filter((r) => r.id !== id)); if (editCvId === id) setEditCvId(null); };

  const updateTrait = (idx, value) => setTraits((ts) => ts.map((t, i) => i === idx ? value : t));
  const deleteTrait = (idx) => { setTraits((ts) => ts.filter((_, i) => i !== idx)); if (editTraitIdx === idx) setEditTraitIdx(null); };
  const addTrait = (value) => { setTraits((ts) => [...ts, value]); setAddingTrait(false); };
  const adoptTraits = (labels) => setTraits(labels);

  const ready = !loading && !error && gems && threads && stats && cv && traits;

  const cvCardRef = useRef(null);
  useLayoutEffect(() => {
    if (editCvId) cvCardRef.current?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
  }, [editCvId]);

  return (
    <DetailShell eyebrow="02 · Proposition Builder" title="Brand Builder" onClose={onClose}>
      {loading && <div className="tiny">Loading…</div>}
      {error && <div className="tiny" style={{ color: 'var(--danger)' }}>Could not load brand data</div>}
      {ready && (
        <>
          {/* Row 1 — Hidden Gems · Golden Threads */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

            <div className="card">
              <SectionTitle
                label="Hidden gems"
                meta={`${gems.length} SURFACED`}
                onPlay={vp.open}
                video={{
                  eyebrow: EYE, chapter: '01', duration: '02:12',
                  title: 'Hidden gems — what others see that you don\'t',
                  description: 'How to read survey replies for the moments that surprise you. The line you keep dismissing is usually the one to lead with.',
                  chips: ['Self-perception', 'Surveys', 'Positioning'],
                }}
              />
              <div style={{ display: 'grid', gap: 0, border: '1px solid var(--ink)' }}>
                {gems.map((g, i) =>
                  editGemId === g.id ? (
                    <div
                      key={g.id}
                      style={{ padding: '10px 12px', borderTop: i === 0 ? '0' : '1px solid var(--hairline)' }}
                    >
                      <ItemForm
                        initialTitle={g.title}
                        initialBody={g.body}
                        titlePlaceholder="Gem title"
                        bodyPlaceholder="Evidence (HTML supported)"
                        saveLabel="Save"
                        onSave={(title, body) => { updateGem(g.id, { title, body }); setEditGemId(null); }}
                        onCancel={() => setEditGemId(null)}
                      />
                    </div>
                  ) : (
                    <div
                      key={g.id}
                      className="lifestage-cell"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 12px',
                        borderTop: i === 0 ? '0' : '1px solid var(--hairline)',
                        cursor: g.body ? 'help' : 'default',
                      }}
                    >
                      <span className="mono" style={{ color: 'var(--ink-4)', fontSize: 10, letterSpacing: '0.1em', minWidth: 18 }}>{String(i + 1).padStart(2, '0')}</span>
                      <span style={{ fontFamily: 'var(--serif)', fontSize: 15, letterSpacing: '-0.01em', color: 'var(--accent-ink)', fontWeight: 600, lineHeight: 1.2, flex: 1 }}>
                        {g.title || <span style={{ color: 'var(--ink-4)', fontFamily: 'var(--sans)', fontSize: 13 }}>Untitled gem</span>}
                      </span>
                      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                        <button style={ICON_BTN} title="Edit" onClick={(e) => { e.stopPropagation(); setAddingGem(false); setEditGemId(g.id); }}>✎</button>
                        <button style={{ ...ICON_BTN, color: 'var(--danger)' }} title="Delete" onClick={(e) => { e.stopPropagation(); deleteGem(g.id); }}>×</button>
                      </div>
                      {g.body && (
                        <div className="lifestage-tip tip-side" role="tooltip">
                          <div className="lifestage-tip-eyebrow">{String(i + 1).padStart(2, '00')} · Hidden gem</div>
                          <div className="lifestage-tip-body">{g.body}</div>
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
              {addingGem
                ? <ItemForm
                    standalone
                    titlePlaceholder="Gem title"
                    bodyPlaceholder="Evidence (HTML supported)"
                    saveLabel="Add"
                    onSave={commitGem}
                    onCancel={() => setAddingGem(false)}
                  />
                : <AddBtn label="+ Add gem" onClick={() => { setEditGemId(null); setAddingGem(true); }} />
              }
            </div>

            <div className="card">
              <SectionTitle
                label="Golden threads"
                meta={`${threads.length} STABLE`}
                onPlay={vp.open}
                video={{
                  eyebrow: EYE, chapter: '02', duration: '02:46',
                  title: 'Golden threads — what shows up in every chapter',
                  description: 'Patterns that survive every role change. How to find yours by triangulating CV, surveys, and your own war stories.',
                  chips: ['Patterns', 'CV review', 'Through-lines'],
                }}
              />
              <div style={{ display: 'grid', gap: 0, border: '1px solid var(--ink)' }}>
                {threads.map((t, i) =>
                  editThreadId === t.id ? (
                    <div
                      key={t.id}
                      style={{ padding: '10px 12px', borderTop: i === 0 ? '0' : '1px solid var(--hairline)' }}
                    >
                      <ItemForm
                        initialTitle={t.title}
                        initialBody={t.body}
                        titlePlaceholder="Thread title"
                        bodyPlaceholder="Evidence (HTML supported)"
                        saveLabel="Save"
                        onSave={(title, body) => { updateThread(t.id, { title, body }); setEditThreadId(null); }}
                        onCancel={() => setEditThreadId(null)}
                      />
                    </div>
                  ) : (
                    <div
                      key={t.id}
                      className="lifestage-cell"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 12px',
                        borderTop: i === 0 ? '0' : '1px solid var(--hairline)',
                        cursor: t.body ? 'help' : 'default',
                      }}
                    >
                      <span className="mono" style={{ color: 'var(--ink-4)', fontSize: 10, letterSpacing: '0.1em', minWidth: 18 }}>{String(i + 1).padStart(2, '0')}</span>
                      <span style={{ fontFamily: 'var(--serif)', fontSize: 15, letterSpacing: '-0.01em', color: 'var(--accent-ink)', fontWeight: 600, lineHeight: 1.2, flex: 1 }}>
                        {t.title || <span style={{ color: 'var(--ink-4)', fontFamily: 'var(--sans)', fontSize: 13 }}>Untitled thread</span>}
                      </span>
                      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                        <button style={ICON_BTN} title="Edit" onClick={(e) => { e.stopPropagation(); setAddingThread(false); setEditThreadId(t.id); }}>✎</button>
                        <button style={{ ...ICON_BTN, color: 'var(--danger)' }} title="Delete" onClick={(e) => { e.stopPropagation(); deleteThread(t.id); }}>×</button>
                      </div>
                      {t.body && (
                        <div className="lifestage-tip tip-side-left" role="tooltip">
                          <div className="lifestage-tip-eyebrow">{String(i + 1).padStart(2, '0')} · Golden thread</div>
                          <div
                            className="lifestage-tip-body"
                            dangerouslySetInnerHTML={{ __html: t.body }}
                          />
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
              {addingThread
                ? <ItemForm
                    standalone
                    titlePlaceholder="Thread title"
                    bodyPlaceholder="Evidence (HTML supported)"
                    saveLabel="Add"
                    onSave={commitThread}
                    onCancel={() => setAddingThread(false)}
                  />
                : <AddBtn label="+ Add thread" onClick={() => { setEditThreadId(null); setAddingThread(true); }} />
              }
            </div>

          </div>

          {/* Row 2 — Character Tracker · Distinctive Character Traits */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

            <div className="card">
              <SectionTitle
                label="Character tracker"
                meta={`${stats.returned}/${stats.sent} RETURNED`}
                onPlay={vp.open}
                video={{
                  eyebrow: EYE, chapter: '04', duration: '01:58',
                  title: 'Running the character survey',
                  description: 'Who to ask, what to ask, and how to chase the last two replies without sounding desperate. The 10/10/10 split and why it matters.',
                  chips: ['Survey design', 'Sample size', 'Follow-up'],
                }}
              />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, alignItems: 'stretch' }}>
                {[['Friends', stats.friends, 10], ['Peers', stats.peers, 10], ['Bosses', stats.bosses, 10]].map(([l, v, m]) => (
                  <div key={l} style={{ border: '1px solid var(--ink)', padding: '18px 12px', textAlign: 'center', display: 'grid', gap: 6, alignContent: 'center' }}>
                    <div style={{ fontFamily: 'var(--serif)', fontSize: 34, lineHeight: 1, letterSpacing: '-0.02em', color: 'var(--accent-ink)' }}>
                      {v}<span style={{ color: 'var(--ink-4)', fontSize: 16 }}>/{m}</span>
                    </div>
                    <div className="tiny" style={{ color: 'var(--ink-3)' }}>{l}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginTop: 10, gap: 8 }}>
                <button
                  onClick={() => setSurvey(true)}
                  className="mono"
                  style={{
                    fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase',
                    padding: '7px 12px', border: '1px solid var(--ink)',
                    background: 'var(--ink)', color: 'var(--paper)', cursor: 'pointer',
                  }}
                >
                  ▶ Run new survey
                </button>
                <button
                  onClick={() => setResults(true)}
                  className="mono"
                  style={{
                    fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase',
                    padding: '7px 12px', border: '1px solid var(--ink)',
                    background: 'transparent', color: 'var(--ink)', cursor: 'pointer',
                  }}
                >
                  ◆ Analyse responses
                </button>
              </div>
            </div>

            <div className="card">
              <SectionTitle
                label="Distinctive character traits"
                meta={`${traits.length} TRAITS`}
                onPlay={vp.open}
                video={{
                  eyebrow: EYE, chapter: '03', duration: '02:34',
                  title: 'Distinctive traits — your top three, ranked',
                  description: 'Why three is the magic number, and how to weight what friends, peers, and bosses say so the signal isn\'t flattened.',
                  chips: ['Friends', 'Peers', 'Bosses', 'Ranking'],
                }}
              />
              <div style={{ display: 'grid', gap: 0, border: '1px solid var(--ink)' }}>
                {traits.map((t, i) =>
                  editTraitIdx === i ? (
                    <div key={i} style={{ padding: '8px 12px', borderTop: i === 0 ? 0 : '1px solid var(--hairline)' }}>
                      <TraitForm
                        initial={t}
                        onSave={(v) => { updateTrait(i, v); setEditTraitIdx(null); }}
                        onCancel={() => setEditTraitIdx(null)}
                      />
                    </div>
                  ) : (
                    <div
                      key={i}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderTop: i === 0 ? 0 : '1px solid var(--hairline)' }}
                    >
                      <span className="mono" style={{ color: 'var(--ink-4)', fontSize: 10, letterSpacing: '0.1em', minWidth: 18 }}>{String(i + 1).padStart(2, '0')}</span>
                      <span style={{ fontFamily: 'var(--serif)', fontSize: 15, letterSpacing: '-0.01em', color: 'var(--accent-ink)', fontWeight: 600, lineHeight: 1.2, flex: 1 }}>
                        {t || <span style={{ color: 'var(--ink-4)', fontFamily: 'var(--sans)', fontSize: 13 }}>Untitled trait</span>}
                      </span>
                      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                        <button style={ICON_BTN} title="Edit" onClick={() => { setAddingTrait(false); setEditTraitIdx(i); }}>✎</button>
                        <button style={{ ...ICON_BTN, color: 'var(--danger)' }} title="Delete" onClick={() => deleteTrait(i)}>×</button>
                      </div>
                    </div>
                  )
                )}
              </div>
              {addingTrait
                ? <TraitForm standalone onSave={addTrait} onCancel={() => setAddingTrait(false)} />
                : <AddBtn label="+ Add trait" onClick={() => { setEditTraitIdx(null); setAddingTrait(true); }} />
              }
            </div>

          </div>

          {/* Row 3 — Extended CV (full width) */}
          <div className="card" ref={cvCardRef}>
            <SectionTitle
              label="Extended CV"
              meta={editCvId ? 'EDITING' : `${cv.length} ROLES`}
              onPlay={vp.open}
              video={{
                eyebrow: EYE, chapter: '05', duration: '03:18',
                title: 'Extended CV — insight & impact, not duties',
                description: 'Rewrite each role as a one-line insight and a one-line impact. The format that turns your CV into raw material for content.',
                chips: ['Insight', 'Impact', 'Storytelling'],
              }}
            />
            {editCvId ? (
              <CvRoleEditor
                role={cv.find((r) => r.id === editCvId)}
                onUpdate={(patch) => updateCv(editCvId, patch)}
                onClose={() => setEditCvId(null)}
                isNew={editCvId.startsWith('cvr_new_')}
                onDiscard={() => { deleteCvRole(editCvId); setEditCvId(null); }}
              />
            ) : (
              <>
                <div className="cv-scroll">
                  <table className="table cv-table">
                    <thead>
                      <tr>
                        <th style={{ width: 28 }}>No.</th>
                        <th style={{ width: 80 }}>Start</th>
                        <th style={{ width: 80 }}>End</th>
                        <th style={{ width: 140 }}>Organisation</th>
                        <th style={{ width: 140 }}>Role</th>
                        <th>Activities</th>
                        <th>Skills</th>
                        <th>Knowledge</th>
                        <th>Experience</th>
                        <th>Insight</th>
                        <th>Impact</th>
                        <th style={{ width: 56 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cv.map((r, i) => {
                        const dash = <span style={{ color: 'var(--ink-4)' }}>—</span>;
                        const cell = (v) => v || dash;
                        return (
                          <tr key={r.id} style={{ verticalAlign: 'top' }}>
                            <td className="mono" style={{ color: 'var(--ink-4)' }}>{i + 1}</td>
                            <td className="mono" style={{ whiteSpace: 'nowrap' }}>{r.start}</td>
                            <td className="mono" style={{ whiteSpace: 'nowrap' }}>{r.end || '—'}</td>
                            <td style={{ fontFamily: 'var(--serif)', fontSize: 14, color: 'var(--accent-ink)', lineHeight: 1.2 }}>{r.org}</td>
                            <td style={{ fontSize: 12, lineHeight: 1.3 }}>{r.title}</td>
                            <td style={{ fontSize: 11, lineHeight: 1.4, color: 'var(--ink-2)' }}>{cell(r.activities)}</td>
                            <td style={{ fontSize: 11, lineHeight: 1.4, color: 'var(--ink-2)' }}>{cell(r.skills)}</td>
                            <td style={{ fontSize: 11, lineHeight: 1.4, color: 'var(--ink-2)' }}>{cell(r.knowledge)}</td>
                            <td style={{ fontSize: 11, lineHeight: 1.4, color: 'var(--ink-2)' }}>{cell(r.experience)}</td>
                            <td style={{ fontSize: 11, lineHeight: 1.4, color: 'var(--ink-2)' }}>{cell(r.insight)}</td>
                            <td style={{ fontSize: 11, lineHeight: 1.4, color: 'var(--ink-2)' }}>{cell(r.impact)}</td>
                            <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                              <button style={ICON_BTN} title="Edit" onClick={() => setEditCvId(r.id)}>✎</button>
                              <button style={{ ...ICON_BTN, color: 'var(--danger)' }} title="Delete" onClick={() => deleteCvRole(r.id)}>×</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <AddBtn label="+ Add role" onClick={addCvRole} />
              </>
            )}
          </div>
        </>
      )}

      {survey && <SurveyFlow onClose={() => setSurvey(false)} />}
      {results && <ResultsOverlay onClose={() => setResults(false)} onAdopt={adoptTraits} />}
      {vp.render()}
    </DetailShell>
  );
}

function ItemForm({ initialTitle = '', initialBody = '', titlePlaceholder = 'Title', bodyPlaceholder = 'Description', saveLabel = 'Add', onSave, onCancel, standalone = false }) {
  const [title, setTitle] = useState(initialTitle);
  const [body, setBody] = useState(initialBody);
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  function handleSubmit(e) {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) return;
    onSave(trimmed, body);
  }
  const fieldStyle = {
    background: 'var(--paper)', border: '1px solid var(--ink)',
    padding: '6px 10px', fontFamily: 'var(--sans)', fontSize: 13,
    color: 'var(--ink)', outline: 'none', width: '100%', boxSizing: 'border-box',
  };
  return (
    <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 6, ...(standalone ? { marginTop: 10 } : {}) }}>
      <input
        ref={ref}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={titlePlaceholder}
        style={fieldStyle}
        onFocus={(e) => (e.target.style.borderColor = 'var(--accent)')}
        onBlur={(e) => (e.target.style.borderColor = 'var(--ink)')}
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={bodyPlaceholder}
        style={{ ...fieldStyle, resize: 'vertical', minHeight: 64 }}
        onFocus={(e) => (e.target.style.borderColor = 'var(--accent)')}
        onBlur={(e) => (e.target.style.borderColor = 'var(--ink)')}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="submit"
          style={{
            fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em',
            textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--paper)',
            border: '1px solid var(--ink)', padding: '6px 12px', cursor: 'pointer',
          }}
        >{saveLabel}</button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em',
            textTransform: 'uppercase', background: 'transparent', color: 'var(--ink-3)',
            border: '1px solid var(--hairline)', padding: '6px 12px', cursor: 'pointer',
          }}
        >Cancel</button>
      </div>
    </form>
  );
}

function AddBtn({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        marginTop: 10, fontFamily: 'var(--mono)', fontSize: 10,
        letterSpacing: '0.08em', textTransform: 'uppercase',
        background: 'transparent', border: '1px dashed var(--ink-3)',
        color: 'var(--ink-3)', padding: '6px 14px', cursor: 'pointer', width: '100%',
      }}
    >
      {label}
    </button>
  );
}

function CvRoleEditor({ role, onUpdate, onClose, isNew = false, onDiscard }) {
  const set = (field) => (e) => onUpdate({ [field]: e.target.value });
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {!isNew && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            style={{
              fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: 'var(--ink-3)', background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 0,
            }}
            onClick={onClose}
          >
            ALL ROLES →
          </button>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <div style={{ ...MONO_LABEL, marginBottom: 4 }}>Start</div>
          <input style={INPUT} value={role.start} onChange={set('start')} placeholder="Mon YY" />
        </div>
        <div>
          <div style={{ ...MONO_LABEL, marginBottom: 4 }}>End</div>
          <input style={INPUT} value={role.end} onChange={set('end')} placeholder="Mon YY (blank if current)" />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <div style={{ ...MONO_LABEL, marginBottom: 4 }}>Organisation</div>
          <input style={INPUT} value={role.org} onChange={set('org')} />
        </div>
        <div>
          <div style={{ ...MONO_LABEL, marginBottom: 4 }}>Role title</div>
          <input style={INPUT} value={role.title} onChange={set('title')} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        {['activities', 'skills', 'knowledge'].map((f) => (
          <div key={f}>
            <div style={{ ...MONO_LABEL, marginBottom: 4 }}>{f.charAt(0).toUpperCase() + f.slice(1)}</div>
            <textarea style={TEXTAREA} value={role[f] || ''} onChange={set(f)} />
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        {['experience', 'insight', 'impact'].map((f) => (
          <div key={f}>
            <div style={{ ...MONO_LABEL, marginBottom: 4 }}>{f.charAt(0).toUpperCase() + f.slice(1)}</div>
            <textarea style={TEXTAREA} value={role[f] || ''} onChange={set(f)} />
          </div>
        ))}
      </div>
      {isNew && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={onClose}
            style={{
              fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em',
              textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--paper)',
              border: '1px solid var(--ink)', padding: '6px 12px', cursor: 'pointer',
            }}
          >Add</button>
          <button
            onClick={onDiscard}
            style={{
              fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em',
              textTransform: 'uppercase', background: 'transparent', color: 'var(--ink-3)',
              border: '1px solid var(--hairline)', padding: '6px 12px', cursor: 'pointer',
            }}
          >Cancel</button>
        </div>
      )}
    </div>
  );
}

function TraitForm({ initial = '', onSave, onCancel, standalone = false }) {
  const [value, setValue] = useState(initial);
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const fieldStyle = {
    background: 'var(--paper)', border: '1px solid var(--ink)',
    padding: '6px 10px', fontFamily: 'var(--sans)', fontSize: 13,
    color: 'var(--ink)', outline: 'none', width: '100%', boxSizing: 'border-box',
  };
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); const v = value.trim(); if (v) onSave(v); }}
      style={{ display: 'grid', gap: 6, ...(standalone ? { marginTop: 10 } : {}) }}
    >
      <input
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Trait name"
        style={fieldStyle}
        onFocus={(e) => (e.target.style.borderColor = 'var(--accent)')}
        onBlur={(e) => (e.target.style.borderColor = 'var(--ink)')}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--paper)', border: '1px solid var(--ink)', padding: '6px 12px', cursor: 'pointer' }}>Save</button>
        <button type="button" onClick={onCancel} style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', background: 'transparent', color: 'var(--ink-3)', border: '1px solid var(--hairline)', padding: '6px 12px', cursor: 'pointer' }}>Cancel</button>
      </div>
    </form>
  );
}

export default BrandDetail;
